import React from 'react';

import { useNavigate } from 'react-router';

import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import ConfirmationModal from '@/components/popups/ConfirmationModal';

import { routes } from 'constants/index';

import {
  trainingModeAtom,
  trainingModeDispatchAtom,
} from '@/atoms/trainingMode';

interface ConfirmationModalsProps {
  isOpen: boolean;
}

const trainingModePickAtom = focusAtom(trainingModeAtom, o =>
  o.pick(['showProgressModal', 'trainingModeData']),
);

const setIsBeforeNavigateAtom = focusAtom(trainingModeAtom, o =>
  o.prop('isBeforeNavigate'),
);

const ConfirmationModals: React.FC<ConfirmationModalsProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const setIsBeforeNavigate = useSetAtom(setIsBeforeNavigateAtom);
  const sendTrainingModeAction = useSetAtom(trainingModeDispatchAtom);
  const {
    showProgressModal,
    trainingModeData: { planId, planName },
  } = useAtomValue(trainingModePickAtom);

  const onExit = () => {
    // This one is only the Train Together Modal
    sendTrainingModeAction('hideProgressModal');
    setIsBeforeNavigate(true);
    sendTrainingModeAction('exitTrainingMode');
    sendTrainingModeAction('resetTrainingModeData');

    if (window.native.isNative) {
      sendNativeMessage({ type: 'trainingModeActive', payload: false });
      sendNativeMessage({ type: 'navigateTo', payload: '/' });
      return;
    }

    navigate({
      pathname: routes.plans.managePlan({ planId }),
    });
  };

  const goToExplorePage = () => {
    setIsBeforeNavigate(true);
    sendTrainingModeAction('exitTrainingMode');
    sendTrainingModeAction('resetTrainingModeData');

    if (window.native.isNative) {
      sendNativeMessage({ type: 'trainingModeActive', payload: false });
      sendNativeMessage({ type: 'navigateTo', payload: '/' });
      return;
    }

    navigate(
      {
        pathname: '/',
      },
      {
        state: { fromTrainingMode: true },
      },
    );
  };

  return (
    <>
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.exitModalBody')}
        headerText={t('TrainingPlans.trainTogether.exitModalHeader')}
        isOpen={showProgressModal}
        onClose={() => sendTrainingModeAction('hideProgressModal')}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onExit}
        primaryButtonText={t('Button.exit')}
        secondaryButtonHandler={() =>
          sendTrainingModeAction('hideProgressModal')
        }
        secondaryButtonText={t('Button.stay')}
      />
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.completedPlanModal', {
          planName: planName,
        })}
        headerText={t('Generic.planCompleted')}
        isOpen={isOpen}
        onClose={goToExplorePage}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={goToExplorePage}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};

export default ConfirmationModals;
